/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Alert,
    AlertIcon,
    Button,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import {useAccessToken, useConfig} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import WithdrawalFields from '@consteed/overrides/components/forms/withdrawal-fields'
import {parseWithdrawalError, submitWithdrawalRequest} from '@consteed/overrides/utils/withdrawal-client'

const WithdrawalForm = ({form, onClose}) => {
    const intl = useIntl()
    const toast = useToast()
    const config = useConfig()
    const {getTokenWhenReady} = useAccessToken()
    const {site} = useMultiSite()

    const submitForm = async (data) => {
        form.clearErrors('global')

        try {
            const accessToken = await getTokenWhenReady()

            await submitWithdrawalRequest({
                config,
                accessToken,
                siteId: site.id,
                formData: {
                    name: data.name,
                    email: data.email,
                    orderNumber: data.orderNumber
                }
            })

            toast({
                title: intl.formatMessage({
                    defaultMessage: 'Your withdrawal request has been sent.',
                    id: 'withdrawal_modal.success.title'
                }),
                status: 'success'
            })
            onClose()
        } catch (error) {
            const errorMessage = await parseWithdrawalError(error)

            form.setError('global', {
                type: 'manual',
                message:
                    errorMessage ||
                    intl.formatMessage({
                        defaultMessage: 'Failed to send withdrawal request.',
                        id: 'withdrawal_modal.error.submit_failed'
                    })
            })
        }
    }

    return (
        <form
            id="withdrawal-form"
            onSubmit={form.handleSubmit(submitForm)}
            data-testid="withdrawal-modal-form"
        >
            <Stack spacing={8}>
                <Text textStyle="mediumXlarge">
                    <FormattedMessage defaultMessage="Withdraw Order" id="withdrawal_modal.title" />
                </Text>
                <Text textStyle="bookSmall">
                    <FormattedMessage
                        defaultMessage="Please enter your name, email address, and order number to withdraw your order."
                        id="withdrawal_modal.description"
                    />
                </Text>
                {form.formState.errors?.global && (
                    <Alert status="error">
                        <AlertIcon color="red.600" boxSize={4} />
                        <Text fontSize="sm" ml={3}>
                            {form.formState.errors.global.message}
                        </Text>
                    </Alert>
                )}
                <WithdrawalFields form={form} />
                <Button type="submit" variant="primary" isLoading={form.formState.isSubmitting}>
                    <FormattedMessage
                        defaultMessage="Withdraw Order"
                        id="withdrawal_modal.button.submit"
                    />
                </Button>
            </Stack>
        </form>
    )
}

WithdrawalForm.propTypes = {
    form: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired
}

export default WithdrawalForm
